/* ============================================================================
   SG-2026 · <sg-shipping-price> Custom Element
   ----------------------------------------------------------------------------
   Rechts: NUR Preis (oder "Kostenlos" / "im Checkout").
   Links: Label + Sub-Label ("Paket · 2–4 Werktage" bzw. "Spedition · 5–7 Werktage").

   Zusaetzlich: dispatched 'sg:cart-total:update' mit dem ermittelten
   Shipping-Wert in Cents, damit <sg-cart-total> den Grand-Total live
   aus Zwischensumme + Versand berechnen kann.

   Reagiert auf:
     - 'sg:shipping-rates:update'  (aus sg-shipping-rates.js)
     - 'cart:refresh'              (Theme-Event)
   ============================================================================ */

(function () {
  'use strict';

  if (customElements.get('sg-shipping-price')) return;

  var DELIVERY = {
    Paket: '2–4 Werktage',
    Spedition: '5–7 Werktage'
  };

  class SgShippingPrice extends HTMLElement {
    constructor() {
      super();
      this._onUpdate = this._onUpdate.bind(this);
      this._onRefresh = this._onRefresh.bind(this);
    }

    connectedCallback() {
      document.addEventListener('sg:shipping-rates:update', this._onUpdate);
      document.addEventListener('cart:refresh', this._onRefresh);

      this._request();

      // Fallback-Timeout: wenn nach 2.5s nichts kam, "no-rate" zeigen
      this._fallbackTimer = setTimeout(() => {
        if (this.dataset.state === 'loading') this._apply(null);
      }, 2500);
    }

    disconnectedCallback() {
      document.removeEventListener('sg:shipping-rates:update', this._onUpdate);
      document.removeEventListener('cart:refresh', this._onRefresh);
      if (this._fallbackTimer) clearTimeout(this._fallbackTimer);
    }

    _onRefresh() {
      this.dataset.state = 'loading';
      this._toggleSlots('loading');
      setTimeout(() => this._request(), 180);
    }

    _onUpdate(e) {
      this._apply(e && e.detail ? e.detail.rates : null);
    }

    _request() {
      if (!window.sgShippingRates) {
        setTimeout(() => this._request(), 250);
        return;
      }
      window.sgShippingRates.getRates().then(rates => this._apply(rates));
    }

    _apply(rates) {
      if (this._fallbackTimer) {
        clearTimeout(this._fallbackTimer);
        this._fallbackTimer = null;
      }

      var sublabelEl = this.querySelector('.sg-shipping-price__sublabel');

      if (!rates || !Array.isArray(rates) || rates.length === 0) {
        this.dataset.state = 'no-rate';
        this._toggleSlots('no-rate');
        this._setSublabel(sublabelEl, '');
        this._dispatchCartTotal(null);
        return;
      }

      var cheapest = rates.slice().sort((a, b) => a.price - b.price)[0];
      var method = this._deriveMethodLabel(cheapest, rates);
      var sub = method && DELIVERY[method]
        ? method + ' · ' + DELIVERY[method]
        : (method || '');

      if (cheapest.price === 0) {
        this.dataset.state = 'rate-free';
        this._toggleSlots('rate-free');
        // Paket-Gratis — Lieferzeit zeigen
        this._setSublabel(sublabelEl, method === 'Spedition' ? sub : ('Paket · ' + DELIVERY.Paket));
        this._dispatchCartTotal(0);
        return;
      }

      this.dataset.state = 'rate-available';
      this._toggleSlots('rate-available');

      var amountEl = this.querySelector('[data-show-on="rate-available"] .sg-shipping-price__amount');
      if (amountEl) amountEl.textContent = this._formatMoney(cheapest.price);

      this._setSublabel(sublabelEl, sub);
      this._dispatchCartTotal(cheapest.price);
    }

    _setSublabel(el, text) {
      if (!el) return;
      if (text) {
        el.textContent = text;
        el.removeAttribute('hidden');
      } else {
        el.textContent = '';
        el.setAttribute('hidden', '');
      }
    }

    _deriveMethodLabel(rate, allRates) {
      const n = (rate.name || '').toLowerCase();
      if (/spedi|freight|palette|lkw/.test(n)) return 'Spedition';
      if (/paket|dhl|dpd|ups|hermes|gls/.test(n)) return 'Paket';
      // Heuristik: hoher Preis -> Spedition
      if (rate.price >= 3000) return 'Spedition';
      // Default: Paket
      return 'Paket';
    }

    _dispatchCartTotal(shippingCents) {
      document.dispatchEvent(new CustomEvent('sg:cart-total:update', {
        detail: { shippingCents: shippingCents }
      }));
    }

    _toggleSlots(activeState) {
      this.querySelectorAll('[data-show-on]').forEach(el => {
        if (el.dataset.showOn === activeState) {
          el.removeAttribute('hidden');
        } else {
          el.setAttribute('hidden', '');
        }
      });
    }

    _formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        try {
          return window.Shopify.formatMoney(cents, window.theme && window.theme.moneyFormat);
        } catch (e) { /* fallthrough */ }
      }
      const amount = cents / 100;
      try {
        return new Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
          maximumFractionDigits: 2
        }).format(amount);
      } catch (e) {
        return amount.toFixed(2) + ' €';
      }
    }
  }

  customElements.define('sg-shipping-price', SgShippingPrice);
})();
